<script lang="ts">
	/**
	 * `/Transactions` — every subscription and every one-time purchase in the store (§13, §14).
	 *
	 * `/Customers/:uuid` already lists a customer's subscriptions and invoices, and that is
	 * the wrong shape for the questions an operator actually arrives with: *which* subscriptions
	 * are past due, what sold this week, who is still on the tier being retired, which purchases
	 * are stuck awaiting payment. None of those can be answered by visiting every customer in turn
	 * — the same reason `/Operations` carries a store-wide delivery list as well as the
	 * per-application one.
	 *
	 * **Every row names its customer and links to them.** A store-wide list whose rows cannot say
	 * whose they are answers the counting question and nothing else, and the next question is
	 * always "whose" — which would send the operator back to searching the customer list by hand.
	 *
	 * Both filters are applied **server-side**. Narrowing a fetched page in the browser would hide
	 * everything past the first one while the count went on reporting rows that are not on screen.
	 *
	 * The shape of this page — `controlpanel` filters, a `Table` of `dataTable` cells, one
	 * `RoutedLink` per cell that leads somewhere, and an `InfiniteLoadElement` sentinel rather than
	 * pager buttons — is `Customers.svelte` and `Email.svelte`'s message queue, deliberately. Two
	 * admin lists that page differently teach the operator two habits for one job.
	 */

	import { onMount } from 'svelte';
	import {
		Button,
		Input,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell
	} from 'flowbite-svelte';
	import {
		EmptyState,
		InfiniteLoadElement,
		PageHeader,
		Panel,
		RoutedLink,
		SelectField,
		SubscriptionStatusBadge,
		TableRowsPlaceholder
	} from '../../components';
	import { listAdminOrders, listAdminSubscriptions } from '../../AppNetwork';
	import { formatAmount } from '../../stores/storefront';
	import { formatCalendarDate, formatInterval } from '../../Utilities';
	import type { AdminSaleOrder, AdminSaleSubscription } from '../../AppTypes';

	const PAGE_SIZE = 25;

	/**
	 * Every status the server can answer with, so a filter cannot ask for one that does not exist.
	 *
	 * `unpaid` and `paused` are in the list because the provider produces them and the design must
	 * not be surprised by either (§9.1) — an operator looking for a stuck subscription is exactly
	 * who needs them filterable.
	 */
	const SUBSCRIPTION_STATUSES = [
		{ value: '', name: 'Every status', description: 'No narrowing by status.' },
		{ value: 'active', name: 'Active', description: 'Billing normally and entitled.' },
		{ value: 'trialing', name: 'Trialing', description: 'In a free trial; the first charge has not happened yet.' },
		{ value: 'past_due', name: 'Past due', description: 'A renewal failed and the provider is retrying; the customer keeps access during grace.' },
		{ value: 'unpaid', name: 'Unpaid', description: 'Retries are exhausted but the provider has not cancelled; nothing more will be attempted automatically.' },
		{ value: 'paused', name: 'Paused', description: 'Billing paused at the provider; no invoices are raised until it resumes.' },
		{ value: 'incomplete', name: 'Incomplete', description: 'The first payment never completed — usually an abandoned or still-settling checkout.' },
		{ value: 'canceled', name: 'Cancelled', description: 'Ended; no further billing and no access.' },
		{ value: 'expired', name: 'Expired', description: 'Ran out (for example an imported term that was not renewed).' }
	];

	const ORDER_STATUSES = [
		{ value: '', name: 'Every status', description: 'No narrowing by status.' },
		{ value: 'paid', name: 'Paid', description: 'Paid in full and not refunded.' },
		{ value: 'pending', name: 'Payment pending', description: 'Bought, but the money has not settled yet (bank debits take days).' },
		{ value: 'failed', name: 'Payment failed', description: 'The payment was declined; nothing was sold.' },
		{ value: 'refunded', name: 'Refunded', description: 'All of the money has gone back to the customer.' },
		{ value: 'partially_refunded', name: 'Partly refunded', description: 'Some of the money has gone back; the rest was kept.' }
	];

	/**
	 * Refunds, as their own filter beside status. A subscription's status never says it was refunded
	 * — a refunded renewal leaves it active — so "which subscriptions had money given back" could not
	 * be asked at all; and a chargeback is a separate question from a refund, the customer taking the
	 * money rather than being given it.
	 */
	const REFUND_FILTERS = [
		{ value: '', name: 'Refunded or not', description: 'No narrowing by refunds.' },
		{ value: 'refunded', name: 'Refunded', description: 'Money was given back against it, in full or in part. A failed refund does not count.' },
		{ value: 'charged_back', name: 'Charged back', description: 'The customer disputed a charge through their bank.' }
	];

	/** Text, never colour alone — a refunded order has to read as refunded in words. */
	const orderStatusCopy: Record<AdminSaleOrder['status'], string> = {
		pending: 'Payment pending',
		paid: 'Paid',
		refunded: 'Refunded',
		partially_refunded: 'Partly refunded',
		failed: 'Payment failed'
	};

	let subscriptions: AdminSaleSubscription[] = [];
	let subscriptionTotal: number = 0;
	let subscriptionStatus: string = '';
	let subscriptionSearch: string = '';
	let subscriptionRefund: string = '';
	let subscriptionsLoading: boolean = true;
	let subscriptionsLoadingMore: boolean = false;
	let subscriptionsError: string = '';
	let subscriptionSentinel: InfiniteLoadElement | undefined;

	let orders: AdminSaleOrder[] = [];
	let orderTotal: number = 0;
	let orderStatus: string = '';
	let orderSearch: string = '';
	let orderRefund: string = '';
	let ordersLoading: boolean = true;
	let ordersLoadingMore: boolean = false;
	let ordersError: string = '';
	let orderSentinel: InfiniteLoadElement | undefined;

	/**
	 * @param reset True to start the list again — a first load, or a changed filter. False appends
	 *              the next page, which is what the sentinel asks for.
	 */
	async function fnLoadSubscriptions(reset: boolean = true): Promise<void> {
		if (reset) {
			subscriptionsLoading = true;
		} else {
			subscriptionsLoadingMore = true;
		}
		subscriptionsError = '';
		try {
			const result = await listAdminSubscriptions({
				status: subscriptionStatus,
				search: subscriptionSearch,
				refund: subscriptionRefund,
				limit: PAGE_SIZE,
				offset: reset ? 0 : subscriptions.length
			});
			subscriptions = reset ? result.items : [...subscriptions, ...result.items];
			subscriptionTotal = result.total;
		} catch (error) {
			subscriptionsError = (error as Error).message;
		} finally {
			subscriptionsLoading = false;
			subscriptionsLoadingMore = false;
		}
	}

	/** The sentinel fires on the false→true edge only, so it is reset after each page. */
	async function fnLoadMoreSubscriptions(): Promise<void> {
		await fnLoadSubscriptions(false);
		subscriptionSentinel?.resetVisibilityTracking();
		subscriptionSentinel?.updateState();
	}

	async function fnLoadOrders(reset: boolean = true): Promise<void> {
		if (reset) {
			ordersLoading = true;
		} else {
			ordersLoadingMore = true;
		}
		ordersError = '';
		try {
			const result = await listAdminOrders({
				status: orderStatus,
				search: orderSearch,
				refund: orderRefund,
				limit: PAGE_SIZE,
				offset: reset ? 0 : orders.length
			});
			orders = reset ? result.items : [...orders, ...result.items];
			orderTotal = result.total;
		} catch (error) {
			ordersError = (error as Error).message;
		} finally {
			ordersLoading = false;
			ordersLoadingMore = false;
		}
	}

	async function fnLoadMoreOrders(): Promise<void> {
		await fnLoadOrders(false);
		orderSentinel?.resetVisibilityTracking();
		orderSentinel?.updateState();
	}

	onMount(() => {
		void fnLoadSubscriptions();
		void fnLoadOrders();
	});

	$: hasMoreSubscriptions = !subscriptionsLoading && subscriptions.length < subscriptionTotal;
	$: hasMoreOrders = !ordersLoading && orders.length < orderTotal;

	/** What the plan bills each period, stated whole. Never the per-seat unit price (§9.7). */
	function periodLine(subscription: AdminSaleSubscription): string {
		const money = $formatAmount(subscription.total, subscription.currency);
		if (money === null) return '';
		const period = formatInterval(subscription.interval, subscription.intervalCount);
		return subscription.quantity > 1 ? `${money}${period} · ${subscription.quantity} users` : `${money}${period}`;
	}
</script>

<PageHeader title="Transactions">
	<svelte:fragment slot="description">
		Every subscription and every one-time purchase in the store, across all customers.
	</svelte:fragment>
</PageHeader>

<div class="space-y-6">
	<Panel title="Subscriptions" faIcon="rotate">
		<svelte:fragment slot="titleRight">
			<span class="text-default text-xs">
				{subscriptionTotal} subscription{subscriptionTotal === 1 ? '' : 's'}
			</span>
		</svelte:fragment>

		<div class="controlpanel flex flex-wrap items-center gap-3 mb-4">
			<span class="card-label">Filter</span>
			<SelectField
				size="sm"
				class="w-48"
				items={SUBSCRIPTION_STATUSES}
				bind:value={subscriptionStatus}
				on:change={() => void fnLoadSubscriptions()}
			/>
			<SelectField
				size="sm"
				class="w-48"
				aria-label="Refunds"
				items={REFUND_FILTERS}
				bind:value={subscriptionRefund}
				on:change={() => void fnLoadSubscriptions()}
			/>
			<!-- Submitted rather than filtered as you type: every keystroke would be a query over
			     every subscription in the store. The button is what says so — a search box that only
			     responds to Enter looks broken to anyone who does not press it. -->
			<form class="flex items-center gap-2" on:submit|preventDefault={() => void fnLoadSubscriptions()}>
				<Input
					size="sm"
					class="w-64"
					placeholder="Customer or product"
					aria-label="Search subscriptions"
					bind:value={subscriptionSearch}
				/>
				<Button size="sm" color="alternative" type="submit">Search</Button>
			</form>
		</div>

		{#if subscriptionsError.length > 0}
			<EmptyState
				variant="error"
				title="Subscriptions could not be loaded."
				description={subscriptionsError}
			>
				<Button color="alternative" size="sm" on:click={() => void fnLoadSubscriptions()}>
					Try again
				</Button>
			</EmptyState>
		{:else if !subscriptionsLoading && subscriptions.length === 0}
			<EmptyState
				title="No subscriptions match."
				description="Clear the filters to see every subscription in the store."
			/>
		{:else}
			<Table divClass="relative overflow-x-auto">
				<TableHead>
					<TableHeadCell>Customer</TableHeadCell>
					<TableHeadCell>Product</TableHeadCell>
					<TableHeadCell>Billing</TableHeadCell>
					<TableHeadCell>Status</TableHeadCell>
					<TableHeadCell>Renews</TableHeadCell>
				</TableHead>
				<TableBody>
					{#if subscriptionsLoading}
						<TableRowsPlaceholder rows={5} columns={5} />
					{:else}
						{#each subscriptions as subscription (subscription.uuid)}
							<TableBodyRow>
								<TableBodyCell class="dataTable">
									<!--
										The answer to "whose", and a way to get there — without it the next
										step is searching the customer list by hand.
									-->
									<RoutedLink
										to={`/Customers/${subscription.userUUID}`}
										class="font-medium hover:text-primary-500"
									>
										{subscription.customerName ?? subscription.customerEmail ?? 'Unnamed customer'}
									</RoutedLink>
									{#if subscription.customerEmail !== null && subscription.customerName !== null}
										<div class="text-xs text-default">{subscription.customerEmail}</div>
									{/if}
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									<!--
										To the **admin** view of this subscription, not to the storefront
										page for the product. An operator clicking a row on a support screen
										is asking what can be done about that subscription, and the
										catalogue page can do nothing about it.
									-->
									<RoutedLink
										to={`/Transactions/Subscription/${subscription.uuid}`}
										class="font-medium hover:text-primary-500"
									>
										{subscription.productName}
									</RoutedLink>
								</TableBodyCell>
								<TableBodyCell class="dataTable tabular-nums">
									{periodLine(subscription) || 'Amount unavailable'}
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									<SubscriptionStatusBadge
										status={subscription.status}
										cancelAtPeriodEnd={subscription.cancelAtPeriodEnd}
									/>
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									{#if subscription.currentPeriodEnd !== null}
										{formatCalendarDate(subscription.currentPeriodEnd)}
									{:else}
										<span class="text-default">—</span>
									{/if}
								</TableBodyCell>
							</TableBodyRow>
						{/each}
					{/if}
				</TableBody>
			</Table>

			{#if hasMoreSubscriptions}
				<InfiniteLoadElement
					bind:this={subscriptionSentinel}
					class="w-full"
					on:becomeVisible={() => void fnLoadMoreSubscriptions()}
				>
					{#if subscriptionsLoadingMore}
						<EmptyState variant="loading" compact title="Loading more…" />
					{/if}
				</InfiniteLoadElement>
			{/if}
		{/if}
	</Panel>

	<Panel title="One-time purchases" faIcon="bag-shopping">
		<svelte:fragment slot="titleRight">
			<span class="text-default text-xs">
				{orderTotal} purchase{orderTotal === 1 ? '' : 's'}
			</span>
		</svelte:fragment>

		<div class="controlpanel flex flex-wrap items-center gap-3 mb-4">
			<span class="card-label">Filter</span>
			<SelectField
				size="sm"
				class="w-48"
				items={ORDER_STATUSES}
				bind:value={orderStatus}
				on:change={() => void fnLoadOrders()}
			/>
			<SelectField
				size="sm"
				class="w-48"
				aria-label="Refunds"
				items={REFUND_FILTERS}
				bind:value={orderRefund}
				on:change={() => void fnLoadOrders()}
			/>
			<form class="flex items-center gap-2" on:submit|preventDefault={() => void fnLoadOrders()}>
				<Input
					size="sm"
					class="w-64"
					placeholder="Customer or product"
					aria-label="Search purchases"
					bind:value={orderSearch}
				/>
				<Button size="sm" color="alternative" type="submit">Search</Button>
			</form>
		</div>

		{#if ordersError.length > 0}
			<EmptyState variant="error" title="Purchases could not be loaded." description={ordersError}>
				<Button color="alternative" size="sm" on:click={() => void fnLoadOrders()}>Try again</Button>
			</EmptyState>
		{:else if !ordersLoading && orders.length === 0}
			<EmptyState
				title="No purchases match."
				description="Clear the filters to see every purchase in the store."
			/>
		{:else}
			<Table divClass="relative overflow-x-auto">
				<TableHead>
					<TableHeadCell>Customer</TableHeadCell>
					<TableHeadCell>Product</TableHeadCell>
					<TableHeadCell>Total</TableHeadCell>
					<TableHeadCell>Status</TableHeadCell>
					<TableHeadCell>Bought</TableHeadCell>
				</TableHead>
				<TableBody>
					{#if ordersLoading}
						<TableRowsPlaceholder rows={5} columns={5} />
					{:else}
						{#each orders as order (order.uuid)}
							<TableBodyRow>
								<TableBodyCell class="dataTable">
									<RoutedLink
										to={`/Customers/${order.userUUID}`}
										class="font-medium hover:text-primary-500"
									>
										{order.customerName ?? order.customerEmail ?? 'Unnamed customer'}
									</RoutedLink>
									{#if order.customerEmail !== null && order.customerName !== null}
										<div class="text-xs text-default">{order.customerEmail}</div>
									{/if}
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									<RoutedLink
										to={`/Transactions/Order/${order.uuid}`}
										class="font-medium hover:text-primary-500"
									>
										{order.productName}
									</RoutedLink>
								</TableBodyCell>
								<TableBodyCell class="dataTable tabular-nums">
									<!-- Formatted from the whole currency row, never ÷100 (§10). -->
									{$formatAmount(order.total, order.currency) ?? 'Amount unavailable'}
								</TableBodyCell>
								<TableBodyCell class="dataTable">{orderStatusCopy[order.status]}</TableBodyCell>
								<TableBodyCell class="dataTable">
									{order.paidAt === null
										? formatCalendarDate(order.createdAt)
										: formatCalendarDate(order.paidAt)}
								</TableBodyCell>
							</TableBodyRow>
						{/each}
					{/if}
				</TableBody>
			</Table>

			{#if hasMoreOrders}
				<InfiniteLoadElement
					bind:this={orderSentinel}
					class="w-full"
					on:becomeVisible={() => void fnLoadMoreOrders()}
				>
					{#if ordersLoadingMore}
						<EmptyState variant="loading" compact title="Loading more…" />
					{/if}
				</InfiniteLoadElement>
			{/if}
		{/if}
	</Panel>
</div>
